import {type FeedbackRef, frownIcon, frustratedIcon, grinIcon, smileIcon, UserFeedback} from '@github-ui/user-feedback'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {Link} from '@primer/react'
import {type RefObject, useImperativeHandle, useRef} from 'react'

const ratingOptions = [
  {
    name: 'Hate it',
    value: 'VERY_NEGATIVE' as const,
    icon: frustratedIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Not great',
    value: 'NEGATIVE' as const,
    icon: frownIcon,
    color: 'dissatisfied' as const,
  },
  {
    name: 'It’s ok',
    value: 'POSITIVE' as const,
    icon: smileIcon,
    color: 'satisfied' as const,
  },
  {
    name: 'Love it',
    value: 'VERY_POSITIVE' as const,
    icon: grinIcon,
    color: 'verySatisfied' as const,
  },
]

type Rating = (typeof ratingOptions)[number]['value']
export type FeedbackDialogRef = FeedbackRef<Rating>

type UserFeedbackEvent = {
  subject?: string
  rating: Rating | null
  content?: string
  hostname: string
  path?: string
}

export interface DashboardFeedbackDialogProps {
  title?: string
  subject?: string
  placeholderText?: string
  buttonText?: string
  returnFocusRef?: RefObject<HTMLElement | null>
  /** When true, the default "Give feedback" link is hidden. Use ref to trigger openDialog(). */
  hideButton?: boolean
  ref?: React.Ref<FeedbackDialogRef>
}

export function DashboardFeedbackDialog({
  title,
  subject,
  placeholderText,
  buttonText,
  returnFocusRef,
  hideButton,
  ref,
}: DashboardFeedbackDialogProps) {
  const feedbackDialogRef = useRef<FeedbackDialogRef>(null)

  useImperativeHandle(ref, () => ({
    openDialog: (initialOptionValue?: Rating) => feedbackDialogRef.current?.openDialog(initialOptionValue),
  }))

  const onSubmit = async (rating: Rating | null, text: string): Promise<string[]> => {
    const errors = []

    if (rating == null) {
      errors.push('Please select a rating.')
    }

    const limit = 2000
    if (text.length > limit) {
      errors.push(`Please keep your feedback within ${limit} characters or less.`)
    }

    if (errors.length > 0) {
      return errors
    }

    const body: UserFeedbackEvent = {
      subject,
      rating,
      content: text,
      hostname: window.location.hostname,
      path: window.location.pathname,
    }

    try {
      const response = await verifiedFetchJSON('/dashboard/feedback', {method: 'POST', body})

      if (!response.ok) {
        errors.push('An error occurred while submitting your feedback.')
      }
    } catch {
      errors.push('An error occurred while submitting your feedback.')
    }

    return errors
  }

  return (
    <>
      {!hideButton && (
        <Link as="button" onClick={() => feedbackDialogRef.current?.openDialog()}>
          {buttonText ?? 'Give feedback'}
        </Link>
      )}
      <UserFeedback
        ref={feedbackDialogRef}
        title={title}
        options={ratingOptions}
        placeholderText={placeholderText}
        onSubmit={onSubmit}
        returnFocusRef={returnFocusRef}
      />
    </>
  )
}
